import { useEffect } from 'react';
import type { AreaResponse } from '@/src/lib/api';
import { routing } from '@/src/i18n/routing';

type UseDetectedAreaUrlSyncParams = {
  showBorder: boolean;
  detectedArea: AreaResponse | undefined;
  resourceId: Array<string> | string | undefined;
  searchSlug: Array<string> | string | undefined;
};

function firstRouteValue(value: Array<string> | string | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

function getLocalePrefix(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  return routing.locales.includes(segments[0] as (typeof routing.locales)[number]) ? `/${segments[0]}` : '';
}

/**
 * Updates the browser URL via window.history.replaceState when the user pans
 * the map with the border OFF and a new area is detected. Uses replaceState
 * instead of Next.js router to avoid triggering re-renders.
 */
export function useDetectedAreaUrlSync({
  showBorder,
  detectedArea,
  resourceId,
  searchSlug,
}: UseDetectedAreaUrlSyncParams): void {
  useEffect(() => {
    const currentResourceId = firstRouteValue(resourceId);
    const currentSearchSlug = firstRouteValue(searchSlug);
    if (
      !showBorder &&
      detectedArea &&
      detectedArea.resourceId &&
      currentSearchSlug &&
      detectedArea.resourceId !== currentResourceId
    ) {
      const currentUrl = window.location.pathname + window.location.search;
      const currentParams = new URLSearchParams(window.location.search);
      currentParams.delete('page');

      const newPathname = `${getLocalePrefix(window.location.pathname)}/${detectedArea.resourceId}/${currentSearchSlug}`;
      const newUrl = currentParams.toString() ? `${newPathname}?${currentParams.toString()}` : newPathname;

      if (currentUrl !== newUrl) {
        window.history.replaceState(null, '', newUrl);
      }
    }
  }, [showBorder, detectedArea, resourceId, searchSlug]);
}
